/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { ApolloError, gql, useMutation } from "@apollo/client";

import { useErrorsOrSuccessHandler } from "graphql/errorHandlers";
import {
  ERROR_FRAGMENT,
  ErrorFragment,
  USER_FRAGMENT,
  UserFragment,
} from "graphql/fragments";
import { SESSION, SessionQuery } from "graphql/queries";
import { UpdateUserAttributes } from "graphql/types";

const UPDATE_USER = gql`
  mutation ($attributes: UpdateUserAttributes!) {
    updateUser(input: { attributes: $attributes }) {
      user {
        ...UserFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${USER_FRAGMENT}
  ${ERROR_FRAGMENT}
`;

interface Payload {
  user?: UserFragment | null;
  errors: ErrorFragment[];
}

interface UpdateUserMutation {
  updateUser: Payload | null;
}

export const useUpdateUserMutation = () => {
  const handleErrors = useErrorsOrSuccessHandler();

  const [mutation, { data, loading }] = useMutation<
    UpdateUserMutation,
    { attributes: UpdateUserAttributes }
  >(UPDATE_USER);

  const updateUser = (
    attributes: UpdateUserAttributes,
    onSuccess?: () => void
  ) =>
    mutation({
      variables: { attributes },
      update(cache, { data: userData }) {
        const user = userData?.updateUser?.user;
        if (!user) return;

        cache.modify({
          fields: {
            session() {
              const sessionQuery = cache.readQuery<SessionQuery>({
                query: SESSION,
              });
              if (!sessionQuery) return;

              cache.writeQuery<SessionQuery>({
                query: SESSION,
                data: {
                  session: {
                    ...sessionQuery.session,
                    user,
                  },
                },
              });
            },
          },
        });
      },
    })
      .then(({ data, errors }) => {
        const dataErrors = data?.updateUser?.errors ?? [];

        handleErrors({ dataErrors, graphqlErrors: errors, onSuccess });
      })
      .catch((_error: ApolloError) => null);

  return {
    updateUser,
    isLoading: loading,
    user: data?.updateUser?.user,
    errors: data?.updateUser?.errors ?? [],
  };
};

export const useSilentUpdateUserMutation = () => {
  const [mutation, { data, loading }] = useMutation<
    UpdateUserMutation,
    { attributes: UpdateUserAttributes }
  >(UPDATE_USER);

  const updateUser = (attributes: UpdateUserAttributes) =>
    mutation({ variables: { attributes } });

  return {
    updateUser,
    isLoading: loading,
    user: data?.updateUser?.user,
    errors: data?.updateUser?.errors ?? [],
  };
};
